import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createChatSession, resetChat } from "./redux/AISagas";
import AIHeader from "./smartComponents/AIHeader";
import Message from "./smartComponents/Message";
import MessageComposer from "./smartComponents/MessageComposer";
import AILoader from "./smartComponents/AILoader";
import AIError from "./smartComponents/AIError";
import isEqual from "lodash/isEqual";
import { useScrollToLastMessage } from "./hooks/useScrollToLastMessage";
import { useLocalStorageState } from "./hooks/useLocalStorageState";
import usePdf from "./hooks/usePdf";
import "./ai-page.scss";

const Widget = ({ pageName, filter }) => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useLocalStorageState(`${pageName}-aiWidget-open`, true);
  const [inputMessage, setInputMessage] = useState("");
  const errors = useSelector((state) => state.ai.errors);
  const initialRequest = useSelector((state) => state.ai.initialRequest);
  const loading = useSelector((state) => state.ai.isLoading);
  const messages = useSelector((state) => state.ai.currentSession.messages);

  const messagesEndRef = useRef(null);

  useScrollToLastMessage(messagesEndRef, [messages.length, isOpen]);

  useEffect(() => {
    dispatch(createChatSession({ pageName, filter }));
    return () => dispatch(resetChat());
  }, [filter]);

  const expanded = isOpen && messages.length > 1;

  const pdf = usePdf(messagesEndRef);
  const hasContent = !(loading && initialRequest);

  return (
    <div id="ai-widget" className="ai-agent" data-testid="ai-widget">
      <div className={`ai ${expanded ? "expanded" : ""}`} data-testid="ai-wrapper">
        <AIHeader
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          showLoader={loading}
          exportDetails={isOpen && hasContent && pdf}
          errors={errors}
        />
        {isOpen && (
          <>
            <div className="ai__content" ref={messagesEndRef} data-testid="ai-content">
              {loading && initialRequest ? (
                <AILoader size="large" />
              ) : (
                messages.map((messageItem, idx) => (
                  <Message
                    key={idx}
                    messageItem={messageItem}
                    pageName={pageName}
                    filter={filter}
                    setInputMessage={setInputMessage}
                  />
                ))
              )}

              <AIError />

              {loading && !initialRequest && <AILoader size="small" />}
            </div>

            {!initialRequest && (
              <MessageComposer
                filter={filter}
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                pageName={pageName}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(Widget, (prevProps, nextProps) => {
  return isEqual(prevProps.filter, nextProps.filter);
});
